import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './CreateOffer.css';

function CreateOffer() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    customerAddress: '',
    customerTaxNo: '',
    customerTaxOffice: '',
    contactPerson: '',
    customerPhone: '',
    offerDate: new Date().toISOString().split('T')[0],
    validityDate: '',
    kdvRate: 20,
    introText: '',
    hasMaintenance: false,
    maintenanceStartDate: ''
  });
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    productId: '',
    quantity: 1,
    discountPercent: 0,
    description: ''
  });

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data.data);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data.data);
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    if (customer) {
      setFormData({
        ...formData,
        customerId,
        customerName: customer.name,
        customerAddress: customer.address || '',
        customerTaxNo: customer.tax_no || '',
        customerTaxOffice: customer.tax_office || '',
        contactPerson: customer.contact_person || '',
        customerPhone: customer.phone || '' 
    });
    } else {
      setFormData({
        ...formData,
        customerId: '',
        customerName: '',
        customerAddress: '',
        customerTaxNo: '',
        customerTaxOffice: '',
        contactPerson: '',
        customerPhone: ''
      });
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    if (product) {
      setCurrentItem({
        ...currentItem,
        productId,
        unitPrice: parseFloat(product.unit_price) || 0,
        currency: product.currency || 'EUR',
        description: product.description || ''
      });
    }
  };

  const handleAddItem = () => {
    if (!currentItem.productId) {
      alert('Lütfen ürün seçin');
      return;
    }

    const product = products.find(p => p.id === parseInt(currentItem.productId));
    const quantity = parseInt(currentItem.quantity) || 1;
    const unitPrice = parseFloat(currentItem.unitPrice) || 0;
    const discountPercent = parseFloat(currentItem.discountPercent) || 0;
    const discountedPrice = unitPrice * (1 - discountPercent / 100);
    const totalPrice = quantity * discountedPrice;
    
    const newItem = {
      productId: currentItem.productId,
      productName: product.name,
      productCode: product.code,
      quantity: quantity,
      unitPrice: unitPrice,
      discountPercent: discountPercent,
      totalPrice: totalPrice,
      currency: product.currency || 'EUR',
      description: currentItem.description
    };

    setItems([...items, newItem]);
    setCurrentItem({
      productId: '',
      quantity: 1,
      discountPercent: 0,
      description: ''
    });
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0);
    const kdvAmount = (subtotal * parseFloat(formData.kdvRate)) / 100;
    const total = subtotal + kdvAmount;
    return { subtotal, kdvAmount, total };
  };

  const handleSubmit = async (status = 'draft') => {
    if (!formData.customerId) {
      alert('Lütfen müşteri seçin');
      return;
    }

    if (items.length === 0) {
      alert('Lütfen en az bir ürün ekleyin');
      return;
    }

    // Para birimi kontrolü
    const currencies = [...new Set(items.map(item => item.currency))];
    if (currencies.length > 1) {
      alert('Tüm ürünler aynı para biriminde olmalıdır!');
      return;
    }

    const { subtotal, kdvAmount, total } = calculateTotals();

    const offerData = {
      customer_id: parseInt(formData.customerId),
      customer_name: formData.customerName,
      customer_address: formData.customerAddress,
      customer_tax_no: formData.customerTaxNo,
      customer_tax_office: formData.customerTaxOffice,
      contact_person: formData.contactPerson,
      customer_phone: formData.customerPhone,
      offer_date: formData.offerDate,
      validity_date: formData.validityDate || null,
      currency: currencies[0],
      subtotal,
      kdv_rate: formData.kdvRate,
      kdv_amount: kdvAmount,
      total,
      intro_text: formData.introText,
      has_maintenance: formData.hasMaintenance,
      maintenance_start_date: formData.hasMaintenance ? formData.maintenanceStartDate : null,
      status: status,
      items: items.map((item, index) => ({
        product_id: parseInt(item.productId),
        sequence_no: index + 1,
        quantity: parseInt(item.quantity),
        unit_price: parseFloat(item.unitPrice),
        discount_percent: parseFloat(item.discountPercent),
        total_price: parseFloat(item.totalPrice),
        description: item.description
      }))
    };

    try {
      await api.post('/offers', offerData);
      alert(status === 'draft' ? 'Teklif taslak olarak kaydedildi!' : 'Teklif onaya gönderildi!');
      navigate('/offers');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || 'Teklif oluşturulamadı'));
    }
  };

  const { subtotal, kdvAmount, total } = calculateTotals();
  const currency = items.length > 0 ? items[0].currency : 'EUR';

  return (
    <div className="create-offer-page">
      <div className="page-header">
        <h1>➕ Yeni Teklif Oluştur</h1>
        <button className="btn-back" onClick={() => navigate('/offers')}>
          ← Geri
        </button>
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        <div className="form-section">
          <h2>👤 Müşteri Seçimi</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Müşteri Seçin *</label>
              <select
                value={formData.customerId}
                onChange={(e) => handleCustomerChange(e.target.value)}
                required
              >
                <option value="">Müşteri seçin...</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.customer_code} - {customer.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {formData.customerId && (
          <div className="form-section">
            <h2>📋 Müşteri Bilgileri</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Müşteri Adı</label>
                <input type="text" value={formData.customerName} readOnly />
              </div>
              <div className="form-group">
                <label>İlgili Kişi</label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                  placeholder="İlgili kişi"
                />
              </div>
              <div className="form-group full-width">
                <label>Adres</label>
                <textarea value={formData.customerAddress} readOnly rows="2" />
              </div>
              <div className="form-group">
                <label>Vergi No</label>
                <input type="text" value={formData.customerTaxNo} readOnly />
              </div>
              <div className="form-group">
                <label>Vergi Dairesi</label>
                <input type="text" value={formData.customerTaxOffice} readOnly />
              </div>
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>📅 Teklif Bilgileri</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Teklif Tarihi *</label>
              <input
                type="date"
                value={formData.offerDate}
                onChange={(e) => setFormData({...formData, offerDate: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Geçerlilik Tarihi</label>
              <input
                type="date"
                value={formData.validityDate}
                onChange={(e) => setFormData({...formData, validityDate: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>KDV Oranı (%)</label>
              <input
  type="tel"
  className="border rounded px-2 py-1 w-20 text-center"
  value={currentItem.discountPercent}
  onFocus={(e) => {
    if (e.target.value === '0') e.target.select();
  }}
  onChange={(e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    if (value === '') {
      setCurrentItem({...currentItem, discountPercent: ''});
    } else {
      setCurrentItem({...currentItem, discountPercent: parseFloat(value)});
    }
  }}
  onBlur={(e) => {
    if (e.target.value === '') {
      setCurrentItem({...currentItem, discountPercent: 0});
    }
  }}
/>
            </div>
            <div className="form-group full-width">
              <label>Kapsam / Ön Yazı</label>
              <textarea
                value={formData.introText}
                onChange={(e) => setFormData({...formData, introText: e.target.value})}
                rows="4"
                placeholder="Teklif kapsamı hakkında açıklama..."
              />
            </div>
            <div className="form-group full-width">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.hasMaintenance}
                  onChange={(e) => setFormData({...formData, hasMaintenance: e.target.checked})}
                />
                Bakım Anlaşması Var
              </label>
            </div>
            {formData.hasMaintenance && (
              <div className="form-group">
                <label>Bakım Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={formData.maintenanceStartDate}
                  onChange={(e) => setFormData({...formData, maintenanceStartDate: e.target.value})}
                />
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h2>📦 Ürünler</h2>
          
          <div className="add-product-section">
            <div className="form-grid">
              <div className="form-group">
                <label>Ürün Seçin *</label>
                <select
                  value={currentItem.productId}
                  onChange={(e) => handleProductChange(e.target.value)}
                >
                  <option value="">Ürün seçin...</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.code} - {product.name} ({parseFloat(product.unit_price).toFixed(2)} {product.currency})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Miktar *</label>
  <input
  type="tel"
  className="border rounded px-2 py-1 w-20 text-center"
  value={currentItem.quantity}
  onFocus={(e) => {
    if (e.target.value === '1') e.target.select();
  }}
  onChange={(e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value === '') {
      setCurrentItem({...currentItem, quantity: ''});
    } else {
      setCurrentItem({...currentItem, quantity: parseInt(value)});
    }
  }}
  onBlur={(e) => {
    if (e.target.value === '' || e.target.value === '0') {
      setCurrentItem({...currentItem, quantity: 1});
    }
  }}
  min="1"
/>
              </div>
              <div className="form-group">
                <label>İskonto (%)</label>
                <input
                  type="number"
                  value={currentItem.discountPercent}
                  onChange={(e) => setCurrentItem({...currentItem, discountPercent: parseFloat(e.target.value) || 0})}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div className="form-group full-width">
                <label>Açıklama</label>
                <textarea
                  value={currentItem.description}
                  onChange={(e) => setCurrentItem({...currentItem, description: e.target.value})}
                  rows="2"
                  placeholder="Ürün açıklaması..."
                />
              </div>
            </div>
            <button type="button" className="btn-add" onClick={handleAddItem}>
              ➕ Ürünü Ekle
            </button>
          </div>

          {items.length > 0 && (
            <div className="items-table">
              <table>
                <thead>
                  <tr>
                    <th>S.N.</th>
                    <th>Kod</th>
                    <th>Ürün</th>
                    <th>Açıklama</th>
                    <th>Miktar</th>
                    <th>Birim Fiyat</th>
                    <th>İskonto</th>
                    <th>Toplam</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td><strong>{item.productCode}</strong></td>
                      <td>{item.productName}</td>
                      <td>{item.description || '-'}</td>
                      <td>{item.quantity}</td>
                      <td>{(parseFloat(item.unitPrice) || 0).toFixed(2)} {item.currency}</td>
                      <td>%{(parseFloat(item.discountPercent) || 0).toFixed(2)}</td>
                      <td><strong>{(parseFloat(item.totalPrice) || 0).toFixed(2)} {item.currency}</strong></td>
                      <td>
                        <button type="button" className="btn-remove" onClick={() => handleRemoveItem(index)}>
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="totals-section">
            <div className="totals">
              <div className="total-row">
                <span>Brüt Toplam:</span>
                <strong>{(subtotal || 0).toFixed(2)} {currency}</strong>
              </div>
              <div className="total-row">
                <span>KDV (%{formData.kdvRate}):</span>
                <strong>{(kdvAmount || 0).toFixed(2)} {currency}</strong>
              </div>
              <div className="total-row grand-total">
                <span>GENEL TOPLAM:</span>
                <strong>{(total || 0).toFixed(2)} {currency}</strong>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={() => navigate('/offers')}>
            İptal
          </button>
          <button type="button" className="btn-primary" onClick={() => handleSubmit('draft')}>
            💾 Taslak Olarak Kaydet
          </button>
          <button type="button" className="btn-submit" onClick={() => handleSubmit('pending')}>
            ✓ Onaya Gönder
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateOffer;
